import { useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { useHistoryNavButtons } from '@/lib/history-nav';

function HistoryButton({
    label,
    icon: Icon,
    disabled,
    onClick,
}: {
    label: string;
    icon: LucideIcon;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            disabled={disabled}
            onClick={onClick}
            data-testid={`history-${label.toLowerCase()}`}
            className="grid h-6 w-6 place-items-center rounded-button text-[rgb(var(--color-text-secondary))] transition-colors hover:bg-[rgb(var(--color-sidebar-hover)/0.06)] hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)] disabled:pointer-events-none disabled:opacity-40 pointer-coarse:h-8 pointer-coarse:w-8"
        >
            <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
    );
}

// The Back and Forward buttons of the staff toolbar. They move through the
// history of this tab, so a person can go back to the page that opened the
// current page (for example from a Fact Find to the onboarding case). A thin
// line divides them from the breadcrumb.
export default function HistoryNavButtons() {
    const navigate = useNavigate();
    const { canGoBack, canGoForward } = useHistoryNavButtons();
    return (
        <nav
            aria-label="History"
            className="flex flex-none items-center gap-0.5 border-r border-[rgb(var(--color-divider))] pr-2"
        >
            <HistoryButton
                label="Back"
                icon={ChevronLeft}
                disabled={!canGoBack}
                onClick={() => void navigate(-1)}
            />
            <HistoryButton
                label="Forward"
                icon={ChevronRight}
                disabled={!canGoForward}
                onClick={() => void navigate(1)}
            />
        </nav>
    );
}
